/**
 * offeringMigration.ts
 *
 * Pure helpers for normalising legacy offering data shapes to the current
 * canonical types.  Keeping these as free functions (no React, no side-effects)
 * makes them trivial to unit-test and safe to call in any context.
 */

import type { AssessmentType, CurriculumMilestone } from '@/types/offering';

// ─── CurriculumMilestone helpers ─────────────────────────────────────────────

/**
 * Resolve the effective AssessmentType for a milestone.
 *
 * Backward-compat: old records stored a boolean `hasAssessment` flag instead
 * of an `assessmentType` enum.  Prefer the new field; fall back gracefully.
 */
export function resolveAssessmentType(m: Pick<CurriculumMilestone, 'assessmentType' | 'hasAssessment'>): AssessmentType {
  if (m.assessmentType) return m.assessmentType;
  return m.hasAssessment ? 'QUIZ' : 'NONE';
}

/**
 * Re-number a milestone array so weekNumber === index + 1.
 * Returns a new array; does not mutate the input.
 */
export function renumberMilestones(milestones: CurriculumMilestone[]): CurriculumMilestone[] {
  return milestones.map((m, i) => ({ ...m, weekNumber: i + 1 }));
}

// ─── Crash-course migration ───────────────────────────────────────────────────

/**
 * Normalise a crash-course config object to a `CurriculumMilestone[]`.
 *
 * Supports three shapes:
 *   1. `cc.milestones`  — new canonical field (returned as-is)
 *   2. `cc.weekPlan`    — legacy WeekPlanEntry[] (migrated)
 *   3. null / undefined — returns []
 *
 * Legacy `WeekPlanEntry` mapping:
 *   theme          → title
 *   mockTest=true  → assessmentType 'MOCK_TEST'
 *   mockTest=false → assessmentType 'NONE'
 *   topicIds       → topicIds (preserved; defaults to [])
 */
export function migrateCrashCourseMilestones(cc: any): CurriculumMilestone[] {
  if (!cc) return [];
  if (cc.milestones?.length) return cc.milestones as CurriculumMilestone[];
  return (cc.weekPlan ?? []).map((w: any): CurriculumMilestone => ({
    weekNumber: w.weekNumber,
    title: w.theme ?? '',
    description: '',
    topicIds: w.topicIds ?? [],
    assessmentType: (w.mockTest ? 'MOCK_TEST' : 'NONE') as AssessmentType,
  }));
}
